import { useState, useEffect } from 'react';
import { Home, MessageCircle, CheckCircle, ChevronLeft, ChevronRight, Shield } from 'lucide-react';
import type { ChatSession, CoachingCategory } from '@/types';
import { SimpleXPBar } from '@/components/gamification';
import { CATEGORY_ICONS } from '@/lib/category-icons';

export type AppView = 'home' | 'chat' | 'habits' | 'admin';

interface SidebarProps {
  sessions: ChatSession[];
  selectedCategory: CoachingCategory | null;
  onSelectCategory: (category: CoachingCategory | null) => void;
  activeSessionId: string | null;
  onSelectSession: (sessionId: string) => void;
  onNewChat: () => void;
  onDeleteSession: (sessionId: string) => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  activeView: AppView;
  onViewChange: (view: AppView) => void;
  habitStats?: {
    todayCompleted: number;
    todayTotal: number;
    level: number;
    levelProgress: number;
  };
  isAdmin?: boolean;
  onToggleAdminView?: () => void;
  adminViewMode?: 'admin' | 'user';
}

const CATEGORIES: { id: CoachingCategory; label: string }[] = [
  { id: 'faith', label: 'Faith' },
  { id: 'family', label: 'Family' },
  { id: 'finance', label: 'Finance' },
  { id: 'fitness', label: 'Fitness' },
];

export function Sidebar({
  sessions,
  selectedCategory,
  onSelectCategory,
  activeSessionId,
  onSelectSession,
  onNewChat,
  onDeleteSession,
  isCollapsed,
  onToggleCollapse,
  activeView,
  onViewChange,
  habitStats,
  isAdmin,
  onToggleAdminView,
  adminViewMode,
}: SidebarProps) {
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [sidebarCollapsedLocal, setSidebarCollapsedLocal] = useState(isCollapsed);

  // Persist collapse state to localStorage
  useEffect(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    if (saved !== null) {
      const collapsed = saved === 'true';
      setSidebarCollapsedLocal(collapsed);
      if (collapsed !== isCollapsed) {
        onToggleCollapse();
      }
    }
  }, []);

  const handleToggleCollapse = () => {
    const newState = !sidebarCollapsedLocal;
    setSidebarCollapsedLocal(newState);
    localStorage.setItem('sidebar-collapsed', String(newState));
    onToggleCollapse();
  };

  const handleDelete = (sessionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (deleteConfirm === sessionId) {
      onDeleteSession(sessionId);
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(sessionId);
      setTimeout(() => setDeleteConfirm(null), 3000);
    }
  };

  const filteredSessions = selectedCategory
    ? sessions.filter((s) => s.category === selectedCategory)
    : sessions;

  const renderCategoryIcon = (categoryId: string, className: string) => {
    const Icon = CATEGORY_ICONS[categoryId];
    return Icon ? <Icon className={className} /> : null;
  };

  // ============================================
  // COLLAPSED VIEW
  // ============================================
  if (isCollapsed) {
    return (
      <div className="w-16 h-full bg-charcoal-900 border-r border-charcoal-700/50 flex flex-col">
        {/* Level Badge at TOP */}
        <div className="p-3 border-b border-charcoal-700/50">
          {habitStats && (
            <div className="w-10 h-10 rounded-lg bg-royal-gradient flex items-center justify-center shadow-glow">
              <span className="text-white font-bold text-sm">{habitStats.level}</span>
            </div>
          )}
        </div>

        {/* Vertical Menu */}
        <div className="flex-1 flex flex-col items-center gap-2 py-4">
          <button
            onClick={() => onViewChange('home')}
            className={`p-3 rounded-lg transition-all ${
              activeView === 'home'
                ? 'bg-royal-gradient text-white shadow-glow'
                : 'text-slate-400 hover:bg-charcoal-800 hover:text-white'
            }`}
            title="Home"
          >
            <Home className="w-5 h-5" />
          </button>

          <button
            onClick={() => onViewChange('chat')}
            className={`p-3 rounded-lg transition-all ${
              activeView === 'chat'
                ? 'bg-royal-gradient text-white shadow-glow'
                : 'text-slate-400 hover:bg-charcoal-800 hover:text-white'
            }`}
            title="Chat"
          >
            <MessageCircle className="w-5 h-5" />
          </button>

          <button
            onClick={() => onViewChange('habits')}
            className={`p-3 rounded-lg transition-all relative ${
              activeView === 'habits'
                ? 'bg-royal-gradient text-white shadow-glow'
                : 'text-slate-400 hover:bg-charcoal-800 hover:text-white'
            }`}
            title="Habits"
          >
            <CheckCircle className="w-5 h-5" />
            {habitStats && habitStats.todayTotal > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 text-[10px] rounded-full bg-amber-500 text-white flex items-center justify-center">
                {habitStats.todayCompleted}
              </span>
            )}
          </button>

          {isAdmin && (
            <button
              onClick={() => onViewChange('admin')}
              className={`p-3 rounded-lg transition-all ${
                activeView === 'admin'
                  ? 'bg-royal-gradient text-white shadow-glow'
                  : 'text-slate-400 hover:bg-charcoal-800 hover:text-white'
              }`}
              title="Admin"
            >
              <Shield className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Settings at BOTTOM */}
        <div className="p-3 border-t border-charcoal-700/50 space-y-2">
          <button
            onClick={handleToggleCollapse}
            className="p-3 rounded-lg text-slate-400 hover:bg-charcoal-800 hover:text-white transition-colors w-full"
            title="Expand"
          >
            <ChevronRight className="w-5 h-5 mx-auto" />
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // EXPANDED VIEW
  // ============================================
  return (
    <div className="w-72 h-full bg-charcoal-900 border-r border-charcoal-700/50 flex flex-col">
      {/* Level Gauge at TOP */}
      <div className="p-4 border-b border-charcoal-700/50">
        {habitStats && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-royal-gradient flex items-center justify-center shadow-glow font-heading">
                  <span className="text-white font-bold text-lg">{habitStats.level}</span>
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">Level {habitStats.level}</div>
                  <div className="text-xs text-slate-400">{Math.round(habitStats.levelProgress)}% to next</div>
                </div>
              </div>
            </div>
            <SimpleXPBar progress={habitStats.levelProgress} size="sm" />
          </div>
        )}
      </div>

      {/* Vertical Menu Items */}
      <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
        <button
          onClick={() => onViewChange('home')}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
            activeView === 'home'
              ? 'bg-royal-gradient text-white shadow-glow'
              : 'text-slate-300 hover:bg-charcoal-800 hover:text-white'
          }`}
        >
          <Home className="w-5 h-5" />
          <span className="font-medium">Home</span>
        </button>

        <button
          onClick={() => onViewChange('chat')}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
            activeView === 'chat'
              ? 'bg-royal-gradient text-white shadow-glow'
              : 'text-slate-300 hover:bg-charcoal-800 hover:text-white'
          }`}
        >
          <MessageCircle className="w-5 h-5" />
          <span className="font-medium">Chat</span>
        </button>

        <button
          onClick={() => onViewChange('habits')}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all relative ${
            activeView === 'habits'
              ? 'bg-royal-gradient text-white shadow-glow'
              : 'text-slate-300 hover:bg-charcoal-800 hover:text-white'
          }`}
        >
          <CheckCircle className="w-5 h-5" />
          <span className="font-medium">Habits</span>
          {habitStats && habitStats.todayTotal > 0 && (
            <span className="ml-auto px-2 py-1 text-xs rounded-full bg-amber-500 text-white font-semibold">
              {habitStats.todayCompleted}/{habitStats.todayTotal}
            </span>
          )}
        </button>

        {isAdmin && (
          <button
            onClick={() => onViewChange('admin')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
              activeView === 'admin'
                ? 'bg-royal-gradient text-white shadow-glow'
                : 'text-slate-300 hover:bg-charcoal-800 hover:text-white'
            }`}
          >
            <Shield className="w-5 h-5" />
            <span className="font-medium">Admin</span>
          </button>
        )}

        {/* Chat-specific content */}
        {activeView === 'chat' && (
          <div className="mt-4 pt-4 border-t border-charcoal-700/50 space-y-3">
            <button
              onClick={onNewChat}
              className="w-full px-4 py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-medium transition-colors shadow-glow flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Chat
            </button>

            {/* Category filters */}
            <div className="space-y-1">
              <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2 mb-2">
                Filter by Category
              </div>
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => onSelectCategory(selectedCategory === cat.id ? null : cat.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    selectedCategory === cat.id
                      ? 'bg-charcoal-700 ring-2 ring-amber-500'
                      : 'hover:bg-charcoal-800 text-slate-400 hover:text-white'
                  }`}
                >
                  {renderCategoryIcon(cat.id, 'w-4 h-4')}
                  <span className="text-sm">{cat.label}</span>
                </button>
              ))}
            </div>

            {/* Session list */}
            {filteredSessions.length > 0 && (
              <div className="space-y-1">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider px-2 mb-2">
                  Recent Chats
                </div>
                <div className="space-y-1 max-h-64 overflow-y-auto">
                  {filteredSessions.slice(0, 10).map((session) => (
                    <div
                      key={session.id}
                      onClick={() => onSelectSession(session.id)}
                      className={`group px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                        activeSessionId === session.id
                          ? 'bg-charcoal-700 ring-1 ring-amber-500/50'
                          : 'hover:bg-charcoal-800'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-slate-300 truncate flex-1">
                          {session.title || 'Untitled Chat'}
                        </span>
                        <button
                          onClick={(e) => handleDelete(session.id, e)}
                          className={`opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded ${
                            deleteConfirm === session.id
                              ? 'bg-red-500 text-white'
                              : 'hover:bg-red-500/20 text-red-400'
                          }`}
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </nav>

      {/* Settings at BOTTOM LEFT */}
      <div className="p-4 border-t border-charcoal-700/50 space-y-2">
        {onToggleAdminView && (
          <button
            onClick={onToggleAdminView}
            className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors text-sm ${
              adminViewMode === 'user'
                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                : 'text-slate-400 hover:bg-charcoal-800 hover:text-white'
            }`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            {adminViewMode === 'user' ? 'Exit User View' : 'View as User'}
          </button>
        )}

        <button
          onClick={handleToggleCollapse}
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-400 hover:bg-charcoal-800 hover:text-white transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
          <span className="text-sm font-medium">Collapse Sidebar</span>
        </button>
      </div>
    </div>
  );
}
